/** Experiments across agents: what, where, how good, how complete, how much —
 *  and, given a `selection`, a tick box per row to compare them; with
 *  `actions`, a ⋯ per row to archive or restore it. */

import { sourceName } from "@/features/experiments/status";
import { Link } from "react-router";
import { DataTable, type Column } from "@/components/ui/data-table";
import { Progress } from "@/components/ui/page";
import { formatCount, formatDate, formatMoney, formatScore } from "@/lib/format";
import { completeness, isLive, runProgress } from "./status";
import { ExperimentStatusPill } from "./status-pill";
import type { ExperimentSummary } from "./types";
import { SelectBox, type CompareSelection } from "@/features/compare/use-compare-selection";
import { datasetLabel } from "./dataset-label";
import { ArchivedBadge } from "@/features/archive/archived";
import { ExperimentArchiveMenu } from "./detail/experiment-archive";

export function ExperimentsTable({
  experiments,
  selection,
  actions = false,
}: {
  experiments: ExperimentSummary[];
  selection?: CompareSelection | undefined;
  actions?: boolean | undefined;
}) {
  const columns: Column<ExperimentSummary>[] = [
    ...(selection
      ? [
          {
            header: <span className="sr-only">Select</span>,
            className: "w-8 pr-0!",
            cell: (e: ExperimentSummary) => <SelectBox experiment={e} selection={selection} />,
          },
        ]
      : []),
    {
      header: "Experiment",
      className: "max-w-[340px]",
      cell: (e) => (
        <Link to={`/experiments/${e.id}`} className="block truncate font-semibold hover:underline">
          {e.name}
        </Link>
      ),
    },
    {
      header: "Agent",
      cell: (e) =>
        e.agent ? (
          <Link to={`/agents/${e.agent.id}`} className="text-body hover:underline">
            {e.agent.name}
          </Link>
        ) : (
          <span className="text-muted">{sourceName(e)}</span>
        ),
    },
    {
      header: "Dataset",
      className: "text-muted",
      cell: (e) =>
        e.dataset ? (
          <span className="inline-flex items-center gap-1.5">
            {datasetLabel(e.dataset)}
            {e.dataset.archive && <ArchivedBadge />}
          </span>
        ) : (
          "—"
        ),
    },
    {
      header: "Overall",
      num: true,
      className: "font-semibold",
      cell: (e) => formatScore(e.aggregate?.overall),
    },
    {
      header: "Items",
      className: "text-xs text-muted",
      cell: (e) => {
        if (e.status === "draft") return `${e.size.items} items`;
        return e.runner === "dataset_output"
          ? `${formatCount(e.counts.done)} imported`
          : completeness(e.counts);
      },
    },
    {
      header: "Cost",
      num: true,
      className: "text-muted",
      cell: (e) => (e.status === "draft" ? "—" : formatMoney(e.spend.total_usd)),
    },
    {
      header: "Status",
      cell: (e) => (
        <div className="flex flex-col gap-1.5">
          <span>
            <ExperimentStatusPill experiment={e} />
          </span>
          {isLive(e.status) && (
            <div className="w-28">
              <Progress {...runProgress(e)} label={`${e.name} progress`} />
            </div>
          )}
        </div>
      ),
    },
    {
      header: "Created",
      className: "text-xs text-muted",
      cell: (e) => (
        <>
          {e.created_by}
          <br />
          {formatDate(e.created_at)}
        </>
      ),
    },
    ...(actions
      ? [
          {
            header: <span className="sr-only">Actions</span>,
            headClass: "pin-end w-12",
            className: "pin-end text-right",
            isolated: true,
            cell: (e: ExperimentSummary) => <ExperimentArchiveMenu experiment={e} size="sm" />,
          },
        ]
      : []),
  ];
  return (
    <DataTable
      columns={columns}
      rows={experiments}
      rowProps={(e) => ({ "data-selected": selection?.selected.has(e.id) })}
    />
  );
}
